/**
 * Objectives, phases and obstacles (Systems Brief §1, WP5). Pure helpers called from sim.ts.
 * Boards without goals never reach the goal logic; their obstacles (if any) still work.
 */
import { DEFAULTS } from './defaults.ts';
import { KIT_KINDS, type Board, type BoardCell, type Goal, type KitKind, type Objectives, type Obstacle, type SimEvent, type SimState } from './types.ts';
import { applyKit } from './sim.ts';

function cloneObjectives(o: Objectives): Objectives {
  return {
    goals: o.goals.map((g) => ({ ...g, cell: g.cell ? { ...g.cell } : null })),
    phaseCount: o.phaseCount,
    phase: 0,
    phases: o.phases.map((p) => ({ name: p.name, activations: p.activations ? [...p.activations] : undefined })),
    obstacles: o.obstacles.map((b) => ({ ...b, switchCell: b.switchCell ? { ...b.switchCell } : null })),
    regions: Object.fromEntries(Object.entries(o.regions).map(([k, cells]) => [k, cells.map((c) => ({ ...c }))])),
    pickups: o.pickups.map((p) => ({ ...p })),
    inventory: {},
    nestTurn: false,
  };
}

/** Fresh objectives for a level, with phase 0 activated. Hard dirt cells are marked on the state. */
export function loadObjectives(state: SimState, board: Board, events: SimEvent[]): void {
  state.objectives = cloneObjectives(board.objectives);
  state.hardDirt = new Array(state.width * state.height).fill(0);
  for (const o of state.objectives.obstacles) {
    if (o.type === 'hardDirt') state.hardDirt[o.y * state.width + o.x] = 1;
  }
  activatePhase(state, 0, events, false);
}

export function hasGoals(state: SimState): boolean {
  return state.objectives.goals.length > 0;
}

function idx(state: SimState, x: number, y: number): number {
  return y * state.width + x;
}

/** A raised, unbroken pillar blocks its cell for everyone. */
export function pillarAt(state: SimState, x: number, y: number): Obstacle | undefined {
  return state.objectives.obstacles.find((o) => o.type === 'pillar' && o.active && !o.done && o.x === x && o.y === y);
}

export function obstacleAt(state: SimState, type: Obstacle['type'], x: number, y: number): Obstacle | undefined {
  return state.objectives.obstacles.find((o) => o.type === type && o.x === x && o.y === y);
}

function raise(o: Obstacle): SimEvent {
  return { type: 'obstacleRaised', id: o.id, kind: o.type, x: o.x, y: o.y };
}

/** Enter phase k: activate its obstacles and pickups (by their `phase` or the phase's activation list). */
export function activatePhase(state: SimState, k: number, events: SimEvent[], announce = true): void {
  const ob = state.objectives;
  ob.phase = k;
  if (announce) events.push({ type: 'phaseChanged', phase: k, final: k >= ob.phaseCount - 1 });
  const listed = new Set(ob.phases[k]?.activations ?? []);
  for (const o of ob.obstacles) {
    if (o.active || o.done) continue;
    if (o.phase !== k && !listed.has(o.id)) continue;
    o.active = true;
    switch (o.type) {
      case 'pillar':
        events.push(raise(o));
        break;
      case 'nest':
        state.dug[idx(state, o.x, o.y)] = 1;
        events.push({ type: 'nestOpened', id: o.id, x: o.x, y: o.y });
        break;
      case 'gemSpring':
        o.timer = o.rateTicks;
        events.push(raise(o));
        break;
      default:
        break;
    }
  }
  for (const p of ob.pickups) {
    if (!p.active && !p.collected && (p.phase === k || listed.has(p.type))) p.active = true;
  }
}

function progress(state: SimState, g: Goal, value: number, events: SimEvent[]): void {
  if (g.done) return;
  const next = Math.min(g.target, value);
  if (next === g.progress) return;
  g.progress = next;
  events.push({ type: 'goalProgress', id: g.id, goal: g.type, progress: g.progress, target: g.target });
  if (g.progress >= g.target) {
    g.done = true;
    events.push({ type: 'goalComplete', id: g.id, goal: g.type });
  }
}

function currentGoals(state: SimState): Goal[] {
  return state.objectives.goals.filter((g) => g.phase === state.objectives.phase);
}

/** Hooks from the sim. Only goals of the current phase count. */
export function onGemCollected(state: SimState, x: number, y: number, gemType: number, events: SimEvent[]): void {
  for (const g of currentGoals(state)) {
    if (g.type === 'collect' && (g.gemType < 0 || g.gemType === gemType)) progress(state, g, g.progress + 1, events);
  }
  void x;
  void y;
}

export function onCreatureCrushed(state: SimState, x: number, y: number, events: SimEvent[]): void {
  for (const g of currentGoals(state)) if (g.type === 'crush') progress(state, g, g.progress + 1, events);
  // A Crawler crushed on a switch tile opens the locked gem.
  for (const o of state.objectives.obstacles) {
    if (o.type === 'lockedGem' && !o.unlocked && o.switchCell && o.switchCell.x === x && o.switchCell.y === y) unlock(state, o, 'crush', events);
  }
}

export function onCellDug(state: SimState, x: number, y: number, events: SimEvent[]): void {
  for (const o of state.objectives.obstacles) {
    if (o.type === 'lockedGem' && !o.unlocked && o.switchCell && o.switchCell.x === x && o.switchCell.y === y) unlock(state, o, 'dig', events);
  }
}

function unlock(state: SimState, o: Obstacle, by: 'dig' | 'crush', events: SimEvent[]): void {
  o.unlocked = true;
  events.push({ type: 'lockUnlocked', id: o.id, x: o.x, y: o.y, by });
}

/** A gem is collectable unless it sits in a still-locked case. */
export function gemLocked(state: SimState, x: number, y: number): boolean {
  return state.objectives.obstacles.some((o) => o.type === 'lockedGem' && !o.unlocked && o.x === x && o.y === y);
}

export function markLockedGemCollected(state: SimState, x: number, y: number): void {
  const o = obstacleAt(state, 'lockedGem', x, y);
  if (o) o.done = true;
}

/** Flame (or a ≥2-row sack) hits a pillar. Returns true when the pillar broke. */
export function hitPillar(state: SimState, o: Obstacle, by: 'flame' | 'sack', events: SimEvent[]): boolean {
  if (!o.active || o.done) return false;
  o.hitsLeft = by === 'sack' ? 0 : o.hitsLeft - 1;
  if (o.hitsLeft > 0) {
    events.push({ type: 'obstacleHit', id: o.id, kind: 'pillar', x: o.x, y: o.y, hitsLeft: o.hitsLeft });
    return false;
  }
  breakObstacle(state, o, by, events);
  return true;
}

export function breakObstacle(state: SimState, o: Obstacle, by: 'flame' | 'sack' | 'dig' | 'borer', events: SimEvent[]): void {
  o.done = true;
  o.active = false;
  if (o.type === 'pillar') state.dug[idx(state, o.x, o.y)] = 1;
  events.push({ type: 'obstacleBroken', id: o.id, kind: o.type, x: o.x, y: o.y, by });
  if (o.type === 'pillar') for (const g of currentGoals(state)) if (g.type === 'break') progress(state, g, g.progress + 1, events);
}

/**
 * Hard dirt: the first dig cracks it (the digger is held for one cell-time), the second clears it.
 * Returns true when the cell is now diggable as ordinary dirt.
 */
export function digHardDirt(state: SimState, x: number, y: number, by: 'dig' | 'borer', events: SimEvent[], instant = false): boolean {
  const i = idx(state, x, y);
  const h = state.hardDirt[i];
  if (h === 0) return true;
  const o = obstacleAt(state, 'hardDirt', x, y);
  if (h === 1 && by === 'dig' && !instant) {
    state.hardDirt[i] = 2;
    if (o) {
      o.cracked = true;
      events.push({ type: 'obstacleHit', id: o.id, kind: 'hardDirt', x, y, hitsLeft: 1 });
    }
    return false;
  }
  state.hardDirt[i] = 0;
  if (o) breakObstacle(state, o, by, events);
  return true;
}

export function isHardDirt(state: SimState, x: number, y: number): boolean {
  return state.hardDirt[idx(state, x, y)] === 1;
}

/** Rescue goals complete when Pip arrives on the buried cell. */
export function onPlayerArrive(state: SimState, x: number, y: number, events: SimEvent[]): void {
  for (const g of currentGoals(state)) if (g.type === 'rescue' && g.cell && g.cell.x === x && g.cell.y === y) progress(state, g, 1, events);
}

/** Pickup on the player's cell (inert in WP5: counted into the inventory). */
export function collectPickup(state: SimState, x: number, y: number, events: SimEvent[]): void {
  for (const p of state.objectives.pickups) {
    if (p.active && !p.collected && p.x === x && p.y === y) {
      p.collected = true;
      p.active = false;
      state.objectives.inventory[p.type] = (state.objectives.inventory[p.type] ?? 0) + 1;
      events.push({ type: 'pickupCollected', kind: p.type, x, y });
      if ((KIT_KINDS as readonly string[]).includes(p.type)) applyKit(state, p.type as KitKind, 'pickup', events);
    }
  }
}

/**
 * A current-phase goal that can no longer complete, or null. Crush: the creature total is spent, none
 * are alive and no bonus replacement is pending. (Collect, break and clear are bounded statically at
 * load; survive and rescue always complete.)
 */
export function impossibleGoal(state: SimState): Goal | null {
  if (state.bonusActive || state.bonusItem) return null;
  for (const g of currentGoals(state)) {
    if (g.done || g.type !== 'crush') continue;
    if (state.spawned >= state.total && state.creatures.length === 0) return g;
  }
  return null;
}

/** Open nest for the next spawn (alternating with the main spawn), or null. */
export function nextSpawnPoint(state: SimState): { cell: BoardCell; nest: Obstacle | null } {
  const nests = state.objectives.obstacles.filter((o) => o.type === 'nest' && o.active);
  if (nests.length === 0) return { cell: state.spawn, nest: null };
  state.objectives.nestTurn = !state.objectives.nestTurn;
  if (!state.objectives.nestTurn) return { cell: state.spawn, nest: null };
  const n = nests[0];
  return { cell: { x: n.x, y: n.y }, nest: n };
}

/** Per-tick: springs, survive timers, clear progress, rescue, phase advance. Returns true on victory. */
export function updateObjectives(state: SimState, events: SimEvent[]): boolean {
  const ob = state.objectives;
  // Gem springs run while any pillar of their phase stands.
  for (const o of ob.obstacles) {
    if (o.type !== 'gemSpring' || !o.active) continue;
    const standing = ob.obstacles.some((p) => p.type === 'pillar' && p.active && !p.done && p.phase === o.phase);
    if (!standing) continue;
    if (--o.timer > 0) continue;
    o.timer = o.rateTicks;
    const i = idx(state, o.x, o.y);
    if (state.gems[i]) continue;
    state.gems[i] = 1;
    state.gemTypes[i] = o.gemType;
    state.gemsRemaining++;
    state.gemsTotal++;
    events.push({ type: 'springSpawn', id: o.id, x: o.x, y: o.y, gemType: o.gemType });
  }
  if (ob.goals.length === 0) return false;
  for (const g of currentGoals(state)) {
    if (g.done) continue;
    switch (g.type) {
      case 'survive':
        progress(state, g, g.progress + 1, events);
        break;
      case 'clear': {
        const cells = ob.regions[g.regionId];
        const dugCount = cells.filter((c) => state.dug[idx(state, c.x, c.y)] === 1).length;
        // Target = dirt cells at load; count dug cells minus those already open then.
        progress(state, g, dugCount - (cells.length - g.target), events);
        break;
      }
      default:
        break;
    }
  }
  if (currentGoals(state).every((g) => g.done)) {
    if (ob.phase >= ob.phaseCount - 1) return true;
    activatePhase(state, ob.phase + 1, events);
  }
  return false;
}

/** Survive goals restart on death (the phase's timer is the pressure). */
export function onPlayerDeath(state: SimState): void {
  for (const g of currentGoals(state)) if (g.type === 'survive' && !g.done) g.progress = 0;
}

export const OBJECTIVE_TICK_MS = DEFAULTS.tickMs;
